import { styled } from 'styled-components';
import {
  Root as NavRoot,
  List as NavList,
  Item as NavItem,
  Link as NavLink,
} from '@radix-ui/react-navigation-menu';
import { mdiMap } from '@mdi/js';
import { Icon } from '@mdi/react';
import IconDash from 'components/icons/IconDashboard';
import { Tooltip } from 'components/ui';
import ProjectTypeIcon from './ProjectTypeIcon';

const IconAnalyst = styled(Icon).attrs({ path: mdiMap })`
  width: 26px;
  height: 26px;
  display: block;
`;

const IconDashboard = styled(IconDash)`
  display: block;
  padding: 3px;
  width: 26px;
  height: 26px;
  min-width: 26px;
  min-height: 26px;
`;

const DEFAULT_COLOR = '#6E88A6';
const DEFAULT_BG_COLOR = '#f4f7fb';

const IconWrapper = styled.a`
  padding: 8px;
  display: flex;
  flex-grow: 0;
  align-items: center;
  cursor: pointer;

  &[data-state='disabled'] {
    cursor: default;
  }
`;

const Wrapper = styled(NavRoot)`
  background-color: ${(props) =>
    props.$styles?.background_color || DEFAULT_BG_COLOR};
  color: ${(props) => props.$styles?.color || DEFAULT_COLOR};
  position: relative;
  width: 50px;
  border-right: 1px solid rgb(217 226 237);
  display: flex;
  flex-direction: column;
  padding: 12px 7px;
  & > ${IconWrapper} {
    /* Note: 40 appended to the color hex indicates 40% opacity */
    background-color: ${(props) => props.$styles?.color || DEFAULT_COLOR}40;
  }
  & ${IconAnalyst} {
    /* trailing BF indicates 75% opacity */
    color: ${(props) => props.$styles?.color || DEFAULT_COLOR}BF;

    /* indicate hover with full opacity */
    &:hover {
      color: ${(props) => props.$styles?.color || DEFAULT_COLOR};
    }
  }
  & ${IconDashboard} {
    /* trailing BF indicates 75% opacity */
    color: ${(props) => props.$styles?.color || DEFAULT_COLOR}BF;
  }
`;

const List = styled(NavList)`
  margin: 0;
  padding: 0;
  text-indent: 0;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  max-width: 100%;

  > li {
    display: flex;
    justify-content: center;
    padding: 12px 6px;
  }
`;

const Link = styled(NavLink)`
  padding: 6px;

  &[role='button'] {
    cursor: pointer;
  }
`;

/**
 * the project type icon rendered first in the Mode Bar
 *
 * @param {object} props -
 * @param {string} props.src - image source for the project type icon
 * @param {string} props.label - project type key
 * @param {string} [props.onClick] - link to Analyst
 */
export function ModeBarIcon({ src, label, onClick }) {
  const dataState = onClick ? '' : 'disabled';
  return (
    <IconWrapper
      data-testid="project-type-icon"
      onClick={onClick}
      data-state={dataState}
    >
      <ProjectTypeIcon src={src} label={label} size="20" />
    </IconWrapper>
  );
}

/**
 * sidebar navigation
 *
 * @param {object} props -
 * @param {React.node} props.children - slot for the project icon
 * @param {boolean} props.hasProject - false if the dashboard was created without a project; true otherwise
 * @param {Function} props.onClickMap - callback to track explore button click
 * @param {object} props.styles - custom styles stored on the project type
 */
export default function ModeBar({
  children,
  hasProject = true,
  onClickMap,
  styles,
}) {
  return (
    <Wrapper $styles={styles} orientation="vertical">
      {children}
      <List>
        {hasProject && (
          <NavItem>
            <Tooltip side="right" content="Map">
              <Link aria-label="map" role="button" onClick={onClickMap}>
                <IconAnalyst />
              </Link>
            </Tooltip>
          </NavItem>
        )}

        <NavItem>
          <Tooltip side="right" content="Dashboards">
            <Link aria-label="dashboards" active>
              <IconDashboard />
            </Link>
          </Tooltip>
        </NavItem>
      </List>
    </Wrapper>
  );
}
